import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../utils/api';

// Query keys
const SUBSCRIPTIONS_QUERY_KEY = 'subscriptions';

// Custom hook for fetching subscriptions
export const useSubscriptions = (page = 1, limit = 10) => {
  return useQuery({
    queryKey: [SUBSCRIPTIONS_QUERY_KEY, page, limit],
    queryFn: () => apiClient.subscriptions.getAll(page, limit),
    keepPreviousData: true, // Keep previous data while fetching new data
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Custom hook for creating a subscription
export const useCreateSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionData) => apiClient.subscriptions.create(subscriptionData),
    onSuccess: () => {
      // Invalidate and refetch subscriptions data
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_QUERY_KEY] });
    },
  });
};

// Custom hook for updating a subscription
export const useUpdateSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.subscriptions.update(id, data),
    onSuccess: () => {
      // Invalidate and refetch subscriptions data
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_QUERY_KEY] });
    },
  });
};

// Custom hook for deleting a subscription
export const useDeleteSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => apiClient.subscriptions.delete(id),
    onSuccess: () => {
      // Invalidate and refetch subscriptions data
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_QUERY_KEY] });
    },
  });
};

// Custom hook for getting a single subscription
export const useSubscriptionById = (id) => {
  return useQuery({
    queryKey: [SUBSCRIPTIONS_QUERY_KEY, id],
    queryFn: () => apiClient.subscriptions.getById(id),
    enabled: !!id, // Only run query if id exists
  });
};
